import React, { useState, useRef } from 'react';
import { UploadIcons, XIcons, FilePlusIcons } from '../../../icons/icons';
import { FeatureCredits, formatResetDate } from '../../../utils/types/credits_types';

interface CandiBankUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (files: FileList | File[]) => void;
  /** This month's CV credits; drives the "N left" line and the over-limit warning. */
  credits?: FeatureCredits | null;
}

export const CandiBankUploadModal: React.FC<CandiBankUploadModalProps> = ({
  isOpen,
  onClose,
  onFilesSelected,
  credits,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const remaining = credits && !credits.unlimited ? credits.remaining ?? 0 : null;
  const exhausted = remaining !== null && remaining <= 0;

  // Surplus files past this month's allowance are marked "blocked" in the upload widget (and
  // toasted) by the upload hook; the modal closes as soon as files are handed over.
  const submit = (files: FileList | File[]) => onFilesSelected(files);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      submit(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      submit(e.target.files);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className='fixed inset-0 z-[1040] bg-slate-900/50 backdrop-blur-xs transition-opacity animate-in fade-in duration-200'
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className='fixed inset-0 z-[1050] flex items-center justify-center p-4'>
        <div
          className='w-full max-w-xl bg-white rounded-3xl shadow-2xl border border-slate-200/80 overflow-hidden transition-all animate-in zoom-in-95 duration-200'
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className='flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/70'>
            <div className='flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-200'>
                <UploadIcons className='h-5 w-5' />
              </div>
              <div>
                <h3 className='text-base font-black text-slate-900 tracking-tight'>
                  Upload CVs to Candi Bank
                </h3>
                <p className='text-xs text-slate-500 font-medium mt-0.5'>
                  Add candidate profiles directly without creating a job opening first
                </p>
              </div>
            </div>

            <button
              type='button'
              onClick={onClose}
              className='rounded-full p-2 text-slate-400 hover:bg-slate-200/70 hover:text-slate-700 transition-colors'
            >
              <XIcons className='h-5 w-5' />
            </button>
          </div>

          {/* Body Dropzone */}
          <div className='p-6 space-y-5'>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`group flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-all duration-200 ${
                isDragOver
                  ? 'border-emerald-500 bg-emerald-50/70 scale-[1.01]'
                  : 'border-slate-300 bg-slate-50/50 hover:border-emerald-500 hover:bg-emerald-50/30'
              }`}
            >
              <input
                ref={fileInputRef}
                type='file'
                multiple
                accept='.pdf,.doc,.docx,.txt'
                className='hidden'
                onChange={handleFileChange}
              />

              <div className='flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100/80 text-emerald-700 group-hover:scale-110 transition-transform mb-3'>
                <FilePlusIcons className='h-7 w-7' />
              </div>

              <h4 className='text-sm font-extrabold text-slate-800 mb-1'>
                Drag & Drop multiple CVs here
              </h4>

              <p className='text-xs text-slate-500 font-medium mb-4 max-w-sm'>
                Select single files, multiple CVs, or entire candidate folders. Supports up to 1,000+ files per batch.
              </p>

              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                className='inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow-md shadow-emerald-200 hover:bg-emerald-700 transition-all'
              >
                Browse Files
              </button>
            </div>

            {/* Credits for this month */}
            {credits && (
              <div
                className={`flex items-center justify-between text-[11px] px-4 py-2.5 rounded-xl border ${
                  exhausted
                    ? 'bg-rose-50 border-rose-200 text-rose-700'
                    : 'bg-emerald-50/70 border-emerald-200 text-emerald-800'
                }`}
                data-testid='upload-credits'
              >
                <span className='font-bold'>
                  {credits.unlimited
                    ? 'Unlimited CV credits'
                    : exhausted
                      ? 'No CV credits left this month'
                      : `${(remaining ?? 0).toLocaleString()} of ${(credits.monthlyLimit ?? 0).toLocaleString()} CV credits left this month`}
                </span>
                <span className='font-medium'>
                  {credits.unlimited ? `${credits.used.toLocaleString()} uploaded` : `resets ${formatResetDate(credits)}`}
                </span>
              </div>
            )}

            {/* Acceptable file types badge footer */}
            <div className='flex items-center justify-between text-[11px] text-slate-500 bg-slate-100/70 px-4 py-2.5 rounded-xl border border-slate-200/60'>
              <span className='font-bold text-slate-700'>Supported Formats:</span>
              <div className='flex items-center gap-1.5 font-mono font-bold text-[10px] text-slate-600'>
                <span className='px-2 py-0.5 bg-white rounded border border-slate-200'>PDF</span>
                <span className='px-2 py-0.5 bg-white rounded border border-slate-200'>DOCX</span>
                <span className='px-2 py-0.5 bg-white rounded border border-slate-200'>DOC</span>
                <span className='px-2 py-0.5 bg-white rounded border border-slate-200'>TXT</span>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className='flex items-center justify-between border-t border-slate-100 px-6 py-4 bg-slate-50/50'>
            <span className='text-[11px] text-slate-400 font-medium'>
              POST /candi-bank/upload
            </span>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors'
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
